import { z } from 'zod';

import { parsePage, type PageResult } from '@/lib/query/page';

// Client-side mirror of the Laravel staff payloads. The Next server parsed
// these before rendering; in the SPA the browser parses them after fetching
// through the API bridge.

const staffVehicleSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  brand: z.string().default(''),
  model: z.string().default(''),
  category: z.string().default(''),
  pricePerDay: z.coerce.number(),
  seats: z.coerce.number().int().default(5),
  transmission: z.string().default('Automatic'),
  bags: z.coerce.number().int().default(2),
  fuelType: z.string().default('Petrol'),
  mpg: z.string().default(''),
  rating: z.coerce.number().default(5),
  reviewsCount: z.coerce.number().int().default(1),
  licensePlate: z.string().default(''),
  status: z.string().default('available'),
  mileage: z.coerce.number().int().default(0),
  features: z.array(z.string()).default([]),
  imageType: z.string().default(''),
  imageUrl: z.string().default(''),
  galleryImages: z.array(z.string()).default([]),
  colorName: z.string().default(''),
  year: z.coerce.number().int().default(2024),
  highlight: z.boolean().default(false),
  listed: z.boolean().default(true),
  location: z.string().default(''),
  visiteTechniqueExpiry: z.string().default(''),
  assuranceExpiry: z.string().default(''),
  vignetteExpiry: z.string().default(''),
  lastVidangeKm: z.number().int().nullable().optional(),
  nextVidangeKm: z.number().int().nullable().optional(),
});

const laravelBookingSchema = z.object({
  id: z.string().min(1),
  vehicleId: z.string().min(1),
  vehicleName: z.string().default(''),
  customerName: z.string(),
  customerPhone: z.string(),
  customerEmail: z.string().default(''),
  driverLicense: z.string().default(''),
  pickupLocation: z.string(),
  dropoffLocation: z.string(),
  pickupDate: z.string(),
  dropoffDate: z.string(),
  pickupTime: z.string().default(''),
  dropoffTime: z.string().default(''),
  status: z.string().default('pending'),
  rentalDays: z.coerce.number().int(),
  dailyRate: z.coerce.number(),
  totalPrice: z.coerce.number(),
  protectionPlan: z.string().default('basic'),
  protectionPrice: z.coerce.number().default(0),
  selectedAddOns: z.array(z.string()).default([]),
  addOnsPrice: z.coerce.number().default(0),
  subtotal: z.coerce.number().default(0),
  discount: z.coerce.number().default(0),
  promoCode: z.string().default(''),
  avanceAmount: z.coerce.number().default(0),
  cautionAmount: z.coerce.number().default(0),
  cautionType: z.string().default('none'),
  paymentMethod: z.string().default('cash'),
  notes: z.string().default(''),
  clientId: z.string().nullable().optional(),
  pickupPhotos: z.array(z.string()).default([]),
  returnPhotos: z.array(z.string()).default([]),
  idDocumentPhoto: z.string().default(''),
  pickupMileage: z.number().int().nullable().default(null),
  returnMileage: z.number().int().nullable().default(null),
  pickupFuel: z.string().default(''),
  returnFuel: z.string().default(''),
  returnCharges: z.array(z.object({
    label: z.string(),
    amount: z.coerce.number(),
  })).default([]),
  cautionStatus: z.enum(['held', 'returned', 'partial', 'retained']).default('held'),
  cautionRetained: z.coerce.number().default(0),
  amountDue: z.coerce.number().optional(),
});

export type StaffVehicle = z.infer<typeof staffVehicleSchema>;
export type TenantBooking = z.infer<typeof laravelBookingSchema>;

export function parseStaffVehicle(payload: unknown): StaffVehicle {
  const parsed = staffVehicleSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error('Rental service returned an unexpected vehicle');
  }
  return parsed.data;
}

export function parseStaffVehicles(payload: unknown): StaffVehicle[] {
  if (!Array.isArray(payload)) throw new Error('Rental service returned an unexpected vehicle list');
  return payload.map(parseStaffVehicle);
}

export function parseVehiclePage(payload: unknown): PageResult<StaffVehicle> {
  return parsePage(payload, staffVehicleSchema, 'The fleet');
}

export function parseBookingPage(payload: unknown): PageResult<TenantBooking> {
  return parsePage(payload, laravelBookingSchema, 'The booking list');
}

export function parseTenantBookings(payload: unknown): TenantBooking[] {
  if (!Array.isArray(payload)) throw new Error('Rental service returned an unexpected booking list');
  return payload.map((item) => {
    const parsed = laravelBookingSchema.safeParse(item);
    if (!parsed.success) throw new Error('Rental service returned an unexpected booking');
    return parsed.data;
  });
}
